import type { LabReport, LongitudinalBiomarkerData, BiomarkerResult } from '../../types';
import type { CaptainBiomarkerResult, CaptainResponse } from '../../types/captainPipeline';
import { CAPTAIN_SCHEMA_VERSION } from '../../types/captainPipeline';
import { runLabAnalyst } from '../agents/labAnalyst';
import { enforceRequiredConsents } from '../../utils/consentEnforcement';
import { getSessionId } from '../../utils/sessionId';
import { getAuthUid } from '../firebase';
import { getPriorBiomarkerResults } from '../longitudinal/biomarkerHistory';
import { persistCaptainResult } from '../captain/handoffService';
import { runIngestionPipeline } from '../labIngestionPipeline';
import type { ResolvedBiomarker, RangeStatus } from '../../types/labIngestion';

type Listener<T> = (data: T) => void;

// ── BiomarkerResult → CaptainBiomarkerResult Bridge ──
// Converts Lab Ingestion Pipeline output to Captain format so downstream
// services (Nurse, Protocol, Gate) receive canonical IDs and catalog ranges.

function mapRangeStatusToCaptain(status: RangeStatus): CaptainBiomarkerResult['status'] {
  switch (status) {
    case 'below': return 'deficient';
    case 'low_normal':
    case 'high_normal':
    case 'optimal': return 'optimal';
    case 'above': return 'high';
    default: return 'optimal';
  }
}

function biomarkerToCaptainResult(resolved: ResolvedBiomarker): CaptainBiomarkerResult {
  return {
    name: resolved.displayName,
    canonicalId: resolved.canonicalId,
    value: resolved.value,
    unit: resolved.unit,
    originalUnit: resolved.originalUnit || resolved.unit,
    standardRange: { min: resolved.ranges.conventional.low, max: resolved.ranges.conventional.high },
    functionalRange: { min: resolved.ranges.functional.low, max: resolved.ranges.functional.high },
    rangeSource: resolved.matchType === 'exact' ? 'functional' : 'fallback',
    status: mapRangeStatusToCaptain(resolved.ranges.functional.status),
    confidence: resolved.matchConfidence,
  };
}

export function buildCaptainResponse(report: LabReport, patientSex?: 'male' | 'female'): CaptainResponse {
  // Run authoritative ingestion pipeline for canonical resolution + range comparison
  const ingestion = runIngestionPipeline(report.biomarkers, patientSex);
  const biomarkers = ingestion.resolvedBiomarkers.map(biomarkerToCaptainResult);
  const lowConfidence = biomarkers.filter(b => b.confidence < 0.80);

  return {
    schemaVersion: CAPTAIN_SCHEMA_VERSION,
    success: true,
    biomarkers,
    extractionStats: {
      totalFound: report.biomarkers.length,
      totalPossible: 130,
      missingCritical: [],
      lowConfidenceMarkers: lowConfidence.map(b => b.name),
    },
    demographicsStatus: {
      source: 'universal',
      demographicsConflict: false,
      demographicsUnknown: true,
      ageAtExtraction: 0,
    },
    processingTime: 0,
    captainModelVersion: 'lab-analyst-bridge-2.0',
    handoffStatus: 'delivered',
    dateExtraction: null,
  };
}

/**
 * Persist Captain result to Firestore so downstream services (Nurse, Protocol)
 * can consume structured biomarker data via onSnapshot.
 * Non-blocking — failure is logged but does not block the UI.
 */
async function persistCaptainOutput(
  report: LabReport,
  sessionId: string,
  patientId: string,
  patientSex?: 'male' | 'female',
): Promise<void> {
  try {
    const captainResponse = buildCaptainResponse(report, patientSex);
    const extractionStatus = captainResponse.extractionStats.lowConfidenceMarkers.length > 0
      ? 'partial' as const
      : 'complete' as const;

    await persistCaptainResult(
      sessionId,
      patientId,
      captainResponse,
      extractionStatus,
    );
    console.log('[AnalysisOrchestrator] Captain result persisted to Firestore', {
      sessionId,
      patientId,
      biomarkerCount: captainResponse.biomarkers.length,
      extractionStatus,
    });
  } catch (err) {
    // Non-blocking: log but don't fail the analysis
    console.error('[AnalysisOrchestrator] Failed to persist Captain result:', err);
  }
}

let analysisPromise: Promise<LabReport> | null = null;
let analysisResult: LabReport | null = null;
let analysisError: string | null = null;
const completeListeners: Listener<LabReport>[] = [];
const errorListeners: Listener<string>[] = [];

function notifyComplete(report: LabReport) {
  analysisResult = report;
  const listeners = [...completeListeners];
  completeListeners.length = 0;
  for (const fn of listeners) fn(report);
}

function notifyError(err: string) {
  analysisError = err;
  const listeners = [...errorListeners];
  errorListeners.length = 0;
  for (const fn of listeners) fn(err);
}

export function startBackgroundAnalysis(file: File, mockMode: boolean, patientId?: string, patientSex?: 'Male' | 'Female') {
  // Reset previous state
  analysisResult = null;
  analysisError = null;

  // Verify consent before processing, then fetch prior data + run analysis
  // Consent check queries the admin-only dataops_consent collection — anonymous users
  // get a permissions error. Cap at 5 seconds so we don't block analysis for minutes.
  const consentCheckWithTimeout = Promise.race([
    enforceRequiredConsents(getSessionId()),
    new Promise<void>((resolve) => setTimeout(() => {
      console.warn('[AnalysisOrchestrator] Consent check timed out (5s) — proceeding');
      resolve();
    }, 5000)),
  ]);

  analysisPromise = consentCheckWithTimeout
    .catch((err) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('permission') || msg.includes('Permission') || msg.includes('PERMISSION')) {
        console.warn('[AnalysisOrchestrator] Consent check skipped (Firestore permissions):', msg);
        return; // Proceed without consent check
      }
      throw err; // Re-throw real consent failures
    })
    .then(async () => {
      // UC-2: Fetch prior biomarker results for longitudinal comparison
      let priorData: LongitudinalBiomarkerData | null = null;
      if (patientId) {
        priorData = await getPriorBiomarkerResults(patientId);
      }
      return runLabAnalyst(file, mockMode, undefined, priorData, patientSex);
    })
    .then(async (report) => {
      // Persist to Firestore as CaptainResponse for downstream consumption
      const sessionId = getSessionId();
      // Use sessionId as patientId fallback for anonymous users (CAP.17 persistence gate)
      const resolvedPatientId = patientId ?? (await getAuthUid().catch(() => null)) ?? sessionId;
      
      const sex = patientSex?.toLowerCase() as 'male' | 'female' | undefined;
      await persistCaptainOutput(report, sessionId, resolvedPatientId, sex);
      
      notifyComplete(report);
      return report;
    })
    .catch((err) => {
      const msg = err instanceof Error ? err.message : 'Analysis failed';
      notifyError(msg);
      throw err;
    });
}

export function onAnalysisComplete(listener: Listener<LabReport>): () => void {
  // If already complete, fire immediately
  if (analysisResult) {
    listener(analysisResult);
    return () => {};
  }
  completeListeners.push(listener);
  return () => {
    const idx = completeListeners.indexOf(listener);
    if (idx >= 0) completeListeners.splice(idx, 1);
  };
}

export function onAnalysisError(listener: Listener<string>): () => void {
  // If already errored, fire immediately
  if (analysisError) {
    listener(analysisError);
    return () => {};
  }
  errorListeners.push(listener);
  return () => {
    const idx = errorListeners.indexOf(listener);
    if (idx >= 0) errorListeners.splice(idx, 1);
  };
}

export function resetAnalysis() {
  analysisPromise = null;
  analysisResult = null;
  analysisError = null;
  completeListeners.length = 0;
  errorListeners.length = 0;
}

export function getAnalysisStatus() {
  if (analysisResult) return 'completed' as const;
  if (analysisError) return 'failed' as const;
  if (analysisPromise) return 'running' as const;
  return 'idle' as const;
}
